/**
 * WellBrowser - Fast well browsing with offline-first architecture
 * 
 * ARCHITECTURE: Matches SearchResults pattern for consistency
 * - Loads ALL wells upfront (from IndexedDB cache or server)
 * - All filtering/drilldown computed client-side
 * - 4-letter drilldown navigation (A → AB → ABC → ABCD)
 * - Offline-first: Works without network once cached
 * - Virtual list for smooth scrolling through 139K+ wells
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Skeleton,
  Snackbar,
  Alert,
  Tooltip,
  Badge,
  Menu,
  MenuItem
} from '@mui/material';
import CyberSpinner from './CyberSpinner';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import StorageIcon from '@mui/icons-material/Storage';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearAllIcon from '@mui/icons-material/ClearAll';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MapIcon from '@mui/icons-material/Map';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import GoogleIcon from '@mui/icons-material/Google';
import ShareIcon from '@mui/icons-material/Share';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
// Apple Maps and Waze removed - only Google Maps supports waypoints
import { List, useListRef } from 'react-window';

import { Well } from '../types';
import { cacheService } from '../services/cache';
import { parseCompactWellsStream } from '../services/compactParser';
import { telegram } from '../services/telegram';
import { useAppStore } from '@stores/appStore';
import { getCyberColors } from '../theme/cyberColors';

// Status colors helper - same as SearchResults
const getStatusColors = (status?: string) => {
  const s = (status || '').toLowerCase();
  // Active/Producing - Green glow
  if (s.includes('active') || s.includes('producing') || s.includes('drilling') || s.includes('completed') || s.includes('new')) {
    return { bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.5)', hoverBg: 'rgba(34, 197, 94, 0.25)', hoverBorder: 'rgba(34, 197, 94, 0.8)', glow: '0 0 15px rgba(34, 197, 94, 0.25)', dot: '#22c55e', text: '#4ade80', dotGlow: '0 0 6px #22c55e' };
  }
  // Shut-in/Suspended - Orange
  if (s.includes('shut') || s.includes('suspend') || s.includes('si') || s.includes('inactive')) {
    return { bg: 'rgba(251, 146, 60, 0.15)', border: 'rgba(251, 146, 60, 0.5)', hoverBg: 'rgba(251, 146, 60, 0.25)', hoverBorder: 'rgba(251, 146, 60, 0.8)', glow: '0 0 15px rgba(251, 146, 60, 0.25)', dot: '#fb923c', text: '#fdba74', dotGlow: '0 0 6px #fb923c' };
  }
  // Plugged/Abandoned - Red
  if (s.includes('plugged') || s.includes('abandoned') || s.includes('p&a') || s.includes('ta')) {
    return { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.5)', hoverBg: 'rgba(239, 68, 68, 0.25)', hoverBorder: 'rgba(239, 68, 68, 0.8)', glow: '0 0 15px rgba(239, 68, 68, 0.25)', dot: '#ef4444', text: '#f87171', dotGlow: '0 0 6px #ef4444' };
  }
  // Cancelled/Denied - Purple
  if (s.includes('cancel') || s.includes('denied') || s.includes('expired') || s.includes('void') || s.includes('released')) {
    return { bg: 'rgba(168, 85, 247, 0.15)', border: 'rgba(168, 85, 247, 0.5)', hoverBg: 'rgba(168, 85, 247, 0.25)', hoverBorder: 'rgba(168, 85, 247, 0.8)', glow: '0 0 15px rgba(168, 85, 247, 0.25)', dot: '#a855f7', text: '#c084fc', dotGlow: '0 0 6px #a855f7' };
  }
  // Default - Blue/Gray
  return { bg: 'rgba(96, 165, 250, 0.10)', border: 'rgba(96, 165, 250, 0.30)', hoverBg: 'rgba(96, 165, 250, 0.20)', hoverBorder: 'rgba(96, 165, 250, 0.60)', glow: '0 0 15px rgba(96, 165, 250, 0.15)', dot: '#94a3b8', text: '#94a3b8', dotGlow: 'none' };
};

// Constants
const ITEM_HEIGHT = 148; // 3-row layout matching SearchResults (name, chips, buttons)

// Helper to get well name (handles both 'wellName' and 'name' fields from API)
const getWellName = (well: Well | any): string => well?.wellName || well?.name || '';
const getWellApi = (well: Well | any): string => well?.api || well?.identifier || '';

interface WellBrowserProps {
  open: boolean;
  onClose: () => void;
  onSelectWell: (well: Well) => void;
  onViewOnMap?: (well: Well) => void;
  onToggleFavorite: (well: Well) => void;
}



export function WellBrowser({ open, onClose, onSelectWell, onViewOnMap, onToggleFavorite }: WellBrowserProps) {
  const isFavorite = useAppStore((state) => state.isFavorite);
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const cyber = getCyberColors(darkMode, highContrast);
  const listRef = useListRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // State - Load ALL wells upfront (like SearchResults) for proper 4-letter drilldown
  const [allWells, setAllWells] = useState<Well[]>([]); // Complete dataset loaded upfront
  const [dataSource, setDataSource] = useState<'cache' | 'server'>('cache'); // Track data source
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  
  // Multi-letter drilldown state (like SearchResults)
  const [secondCounts, setSecondCounts] = useState<Record<string, Record<string, number>>>({});
  const [thirdCounts, setThirdCounts] = useState<Record<string, Record<string, Record<string, number>>>>({});
  const [fourthCounts, setFourthCounts] = useState<Record<string, Record<string, Record<string, Record<string, number>>>>>({});
  const [selectedSecondLetter, setSelectedSecondLetter] = useState<string>('');
  const [selectedThirdLetter, setSelectedThirdLetter] = useState<string>('');
  const [selectedFourthLetter, setSelectedFourthLetter] = useState<string>('');
  type LetterStage = 'first' | 'second' | 'third' | 'fourth';
  const letterStages: LetterStage[] = ['first', 'second', 'third', 'fourth'];
  const [letterStage, setLetterStage] = useState<LetterStage>('first');
  const [letterExpanded, setLetterExpanded] = useState(true);
  
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [listHeight, setListHeight] = useState(400);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  
  // Filter state (matching SearchResults)
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countyFilter, setCountyFilter] = useState<string>('all');
  const [operatorFilter, setOperatorFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [countyAnchorEl, setCountyAnchorEl] = useState<null | HTMLElement>(null);
  const [operatorAnchorEl, setOperatorAnchorEl] = useState<null | HTMLElement>(null);
  
  // Floating letter badge state
  const [currentLetter, setCurrentLetter] = useState('');
  const [showLetterBadge, setShowLetterBadge] = useState(false);
  const badgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Apply status/county/operator filters (matching SearchResults)
  const filteredWells = useMemo(() => {
    let filtered = allWells.filter(well => {
      const statusMatch = statusFilter === 'all' || well.status?.toLowerCase() === statusFilter.toLowerCase();
      const countyMatch = countyFilter === 'all' || well.county?.toLowerCase() === countyFilter.toLowerCase();
      const operatorMatch = operatorFilter === 'all' || well.operator?.toLowerCase() === operatorFilter.toLowerCase();
      return statusMatch && countyMatch && operatorMatch;
    });
    
    // Sort by well name
    if (sortOrder === 'desc') {
      filtered = [...filtered].sort((a, b) => getWellName(b).localeCompare(getWellName(a)));
    } else {
      filtered = [...filtered].sort((a, b) => getWellName(a).localeCompare(getWellName(b)));
    }
    
    return filtered;
  }, [allWells, statusFilter, countyFilter, operatorFilter, sortOrder]);
  
  // Get unique values for filters (from unfiltered allWells)
  const uniqueStatuses = useMemo(() => Array.from(new Set(allWells.map(well => well.status).filter(Boolean))).sort(), [allWells]);
  const uniqueCounties = useMemo(() => Array.from(new Set(allWells.map(well => well.county).filter(Boolean))).sort(), [allWells]);
  const uniqueOperators = useMemo(() => Array.from(new Set(allWells.map(well => well.operator).filter(Boolean))).sort(), [allWells]);
  
  // Count active filters
  const activeFiltersCount = [statusFilter, countyFilter, operatorFilter].filter(f => f !== 'all').length;

  // Helper to get letter key (uppercase or # for non-alpha)
  const getLetterKey = (name: string): string => {
    if (!name) return '#';
    const first = name.charAt(0).toUpperCase();
    return /[A-Z]/.test(first) ? first : '#';
  };

  // Compute multi-letter drilldown counts from wells
  const letterStats = useMemo(() => {
    const lCounts: Record<string, number> = {};
    const sCounts: Record<string, Record<string, number>> = {};
    const tCounts: Record<string, Record<string, Record<string, number>>> = {};
    const fCounts: Record<string, Record<string, Record<string, Record<string, number>>>> = {};

    filteredWells.forEach((well) => {
      const name = getWellName(well);
      const letter = getLetterKey(name);
      lCounts[letter] = (lCounts[letter] || 0) + 1;

      if (letter === '#') return;

      const secondChar = name.charAt(1);
      if (secondChar) {
        const second = getLetterKey(secondChar);
        sCounts[letter] = sCounts[letter] || {};
        sCounts[letter][second] = (sCounts[letter][second] || 0) + 1;

        const thirdChar = name.charAt(2);
        if (thirdChar) {
          const third = getLetterKey(thirdChar);
          tCounts[letter] = tCounts[letter] || {};
          tCounts[letter][second] = tCounts[letter][second] || {};
          tCounts[letter][second][third] = (tCounts[letter][second][third] || 0) + 1;

          const fourthChar = name.charAt(3);
          if (fourthChar) {
            const fourth = getLetterKey(fourthChar);
            fCounts[letter] = fCounts[letter] || {};
            fCounts[letter][second] = fCounts[letter][second] || {};
            fCounts[letter][second][third] = fCounts[letter][second][third] || {};
            fCounts[letter][second][third][fourth] = (fCounts[letter][second][third][fourth] || 0) + 1;
          }
        }
      }
    });

    return { lCounts, sCounts, tCounts, fCounts };
  }, [filteredWells]);

  // Update counts when letterStats changes
  useEffect(() => {
    setSecondCounts(letterStats.sCounts);
    setThirdCounts(letterStats.tCounts);
    setFourthCounts(letterStats.fCounts);
  }, [letterStats]);

  // Available letters at each stage
  const availableSecondLetters = useMemo(() => {
    if (!selectedLetter) return [];
    const letters = Object.keys(secondCounts[selectedLetter] || {});
    return letters.sort((a, b) => a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b));
  }, [selectedLetter, secondCounts]);

  const availableThirdLetters = useMemo(() => {
    if (!selectedLetter || !selectedSecondLetter) return [];
    const letters = Object.keys(thirdCounts[selectedLetter]?.[selectedSecondLetter] || {});
    return letters.sort((a, b) => a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b));
  }, [selectedLetter, selectedSecondLetter, thirdCounts]);

  const availableFourthLetters = useMemo(() => {
    if (!selectedLetter || !selectedSecondLetter || !selectedThirdLetter) return [];
    const letters = Object.keys(fourthCounts[selectedLetter]?.[selectedSecondLetter]?.[selectedThirdLetter] || {});
    return letters.sort((a, b) => a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b));
  }, [selectedLetter, selectedSecondLetter, selectedThirdLetter, fourthCounts]);

  // Letter stage navigation helpers (matching SearchResults SWD style)
  const letterStageIndex = letterStages.indexOf(letterStage);
  const letterStageLabels: Record<LetterStage, string> = { first: 'Letters', second: '2nd', third: '3rd', fourth: '4th' };
  
  const maxLetterStageIndex = useMemo(() => {
    if (selectedLetter && selectedSecondLetter && selectedThirdLetter) return 3;
    if (selectedLetter && selectedSecondLetter) return 2;
    if (selectedLetter) return 1;
    return 0;
  }, [selectedLetter, selectedSecondLetter, selectedThirdLetter]);

  const handleLetterStageChange = (delta: number) => {
    telegram.hapticFeedback('light');
    const newIndex = Math.max(0, Math.min(letterStageIndex + delta, maxLetterStageIndex));
    setLetterStage(letterStages[newIndex]);
  };

  // Breadcrumb for current letter selection
  const letterBreadcrumb = useMemo(() => {
    if (!selectedLetter) return 'all';
    let bc = selectedLetter;
    if (selectedSecondLetter) bc += selectedSecondLetter;
    if (selectedThirdLetter) bc += selectedThirdLetter;
    if (selectedFourthLetter) bc += selectedFourthLetter;
    return bc;
  }, [selectedLetter, selectedSecondLetter, selectedThirdLetter, selectedFourthLetter]);

  // Final filtered wells by letter drilldown
  const letterFilteredWells = useMemo(() => {
    return filteredWells.filter(well => {
      const name = getWellName(well);
      if (!name) return false;

      // First letter filter
      if (selectedLetter) {
        const firstLetter = getLetterKey(name);
        if (firstLetter !== selectedLetter) return false;
      }

      // Second letter filter
      if (selectedSecondLetter) {
        const secondLetter = name.length >= 2 ? getLetterKey(name.charAt(1)) : '';
        if (secondLetter !== selectedSecondLetter) return false;
      }

      // Third letter filter
      if (selectedThirdLetter) {
        const thirdLetter = name.length >= 3 ? getLetterKey(name.charAt(2)) : '';
        if (thirdLetter !== selectedThirdLetter) return false;
      }

      // Fourth letter filter
      if (selectedFourthLetter) {
        const fourthLetter = name.length >= 4 ? getLetterKey(name.charAt(3)) : '';
        if (fourthLetter !== selectedFourthLetter) return false;
      }

      return true;
    });
  }, [filteredWells, selectedLetter, selectedSecondLetter, selectedThirdLetter, selectedFourthLetter]);

  
  // Track online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Calculate list height
  useEffect(() => {
    if (!open) return;

    const updateHeight = () => {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          setListHeight(containerRef.current.clientHeight);
        }
      });
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [open]);

  // Initialize: Load ALL wells upfront (like SearchResults architecture)
  useEffect(() => {
    if (!open) return;
    
    const loadAllWells = async () => {
      setLoading(true);
      setLoadProgress(0);
      
      try {
        // First try cache (instant, offline-first) - use undefined for "all counties"
        const cachedWells = await cacheService.getCachedData(undefined, 'wells');
        
        if (cachedWells && cachedWells.length > 0) {
          console.log(`📦 WellBrowser: Loaded ${cachedWells.length.toLocaleString()} wells from cache`);
          setDataSource('cache');
          setAllWells(cachedWells);
          setLoading(false);
          return;
        }
        
        // No cache - stream from server (like SearchResults does)
        if (isOnline) {
          console.log('🌐 WellBrowser: No cache, streaming from server via compact format...');
          setDataSource('server');
          
          const response = await fetch('/api/facilities/search?type=wells&format=compact');
          if (!response.ok) {
            throw new Error(`Failed to fetch wells: ${response.statusText}`);
          }
          
          // Use compact parser for efficient streaming of 139K+ wells
          setLoadProgress(0.5); // Indicate loading started
          const wells = await parseCompactWellsStream(response);
          
          console.log(`📡 WellBrowser: Loaded ${wells.length.toLocaleString()} wells from server`);
          setAllWells(wells);
          
          // Cache for offline use
          await cacheService.cacheFacilityType('wells', undefined, wells);
          console.log('💾 WellBrowser: Cached wells for offline use');
        } else {
          console.log('⚠️ WellBrowser: Offline and no cache available');
          setAllWells([]);
        }
      } catch (error) {
        console.error('Failed to load wells:', error);
        setAllWells([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadAllWells();
  }, [open, isOnline]);

  // Handle letter click
  const handleLetterClick = useCallback((letter: string) => {
    setSelectedLetter(letter);
    // Scroll list to top
    if (listRef.current) {
      listRef.current.scrollToRow({ index: 0 });
    }
  }, [listRef]);

  // Handle API number copy
  const handleCopyApi = useCallback((api: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(api);
    telegram.hapticFeedback('light');
    setToast({ open: true, message: `API ${api} copied!` });
  }, []);

  const handleShare = useCallback((well: Well, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`;
    const shareText = `📍 ${getWellName(well)}\nAPI: ${getWellApi(well)}\nOperator: ${well.operator}\nCounty: ${well.county}\nCoords: ${well.latitude.toFixed(4)}, ${well.longitude.toFixed(4)}\n${mapsUrl}`;
    telegram.webApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(mapsUrl)}&text=${encodeURIComponent(shareText)}`);
  }, []);

  const handleOpenPhotos = useCallback((well: Well, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const photosUrl = `https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}&layer=c`;
    window.open(photosUrl, '_blank');
  }, []);

  // Filter handlers (matching SearchResults)
  const handleStatusFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
  };
  const handleStatusFilterClose = () => setStatusAnchorEl(null);
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    handleStatusFilterClose();
    telegram.hapticFeedback('light');
  };
  
  const handleCountyFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setCountyAnchorEl(event.currentTarget);
  };
  const handleCountyFilterClose = () => setCountyAnchorEl(null);
  const handleCountyFilterChange = (county: string) => {
    setCountyFilter(county);
    handleCountyFilterClose();
    telegram.hapticFeedback('light');
  };
  
  const handleOperatorFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setOperatorAnchorEl(event.currentTarget);
  };
  const handleOperatorFilterClose = () => setOperatorAnchorEl(null);
  const handleOperatorFilterChange = (operator: string) => {
    setOperatorFilter(operator);
    handleOperatorFilterClose();
    telegram.hapticFeedback('light');
  };
  
  const handleToggleSort = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    telegram.hapticFeedback('light');
  };

  // Handle rows rendered (for letter badge display during scroll)
  const handleRowsRendered = useCallback((visibleRows: { startIndex: number; stopIndex: number }) => {
    // Update letter badge based on first visible well
    if (letterFilteredWells.length > 0 && visibleRows.startIndex < letterFilteredWells.length) {
      const firstVisibleWell = letterFilteredWells[visibleRows.startIndex];
      const wellName = getWellName(firstVisibleWell);
      const firstChar = wellName.charAt(0).toUpperCase();
      
      if (firstChar && firstChar !== currentLetter) {
        setCurrentLetter(firstChar);
        setShowLetterBadge(true);
        
        // Hide badge after 1.5 seconds
        if (badgeTimeoutRef.current) {
          clearTimeout(badgeTimeoutRef.current);
        }
        badgeTimeoutRef.current = setTimeout(() => {
          setShowLetterBadge(false);
        }, 1500);
      }
    }
  }, [letterFilteredWells, currentLetter]);

  // Render a well row
  const WellRow = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const well = letterFilteredWells[index];
    if (!well) {
      return (
        <Box style={style} sx={{ p: 1 }}>
          <Skeleton variant="rectangular" height={ITEM_HEIGHT - 16} sx={{ borderRadius: 2 }} />
        </Box>
      );
    }

    const favorited = isFavorite(well.id);
    
    // Wells facility color (matches the Wells tab in main navigation)
    const WELLS_COLOR = '#f59e0b'; // Amber - same as facility type button
    
    // Status-based styling
    const statusLower = (well.status || '').toLowerCase();
    const isActiveStatus = statusLower.includes('active') || statusLower.includes('producing') || statusLower.includes('drilling') || statusLower.includes('completed') || statusLower.includes('new');
    const statusColors = getStatusColors(well.status);
    
    // Icon color based on active status
    const iconColor = isActiveStatus ? '#10b981' : statusColors.dot;

    return (
      <Box style={style} sx={{ pl: 1, pr: 7, py: 0.5 }}>
        <Box
          onClick={() => onSelectWell(well)}
          sx={{
            height: ITEM_HEIGHT - 8,
            backgroundColor: darkMode ? '#0f172a' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
            borderRadius: '12px',
            boxShadow: `0 8px 20px ${WELLS_COLOR}35, 0 4px 12px rgba(0,0,0,0.15)`,
            px: 1.25,
            py: 0.75,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            transform: 'scale(1)',
            '&:hover': {
              transform: 'scale(1.02)',
              boxShadow: `0 14px 32px ${WELLS_COLOR}50, 0 8px 20px rgba(0,0,0,0.2)`,
            }
          }}
        >
          {/* ROW 1: Icon + Well Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <LocalGasStationIcon sx={{ 
              fontSize: '1.75rem', 
              color: iconColor, 
              filter: `drop-shadow(0 0 4px ${iconColor}50)`,
              flexShrink: 0 
            }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: cyber.textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '1.05rem',
                lineHeight: 1.25,
                flex: 1,
                minWidth: 0
              }}
            >
              {getWellName(well)}
            </Typography>
          </Box>

          {/* ROW 2: API, Lat/Long, County, Operator chips + Status badge */}
          <Box sx={{ 
            display: 'flex', 
            gap: 0.5, 
            flexWrap: 'wrap', 
            alignItems: 'center',
            overflow: 'hidden',
            maxHeight: 52,
          }}>
            {getWellApi(well) && (
              <Chip
                label={getWellApi(well)}
                size="small"
                icon={<ContentCopyIcon sx={{ fontSize: '0.75rem !important' }} />}
                onClick={(e) => handleCopyApi(getWellApi(well), e)}
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(30, 64, 175, 0.1)',
                  color: cyber.chipBlue,
                  cursor: 'pointer',
                  '& .MuiChip-icon': { color: cyber.chipBlue },
                  '&:hover': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(30, 64, 175, 0.2)' }
                }}
              />
            )}
            {well.latitude && well.longitude && (
              <Chip
                label={`${well.latitude.toFixed(4)}, ${well.longitude.toFixed(4)}`}
                size="small"
                icon={<LocationOnIcon sx={{ fontSize: '0.85rem !important' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`;
                  window.open(mapsUrl, '_blank');
                }}
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(180, 83, 9, 0.1)',
                  color: darkMode ? '#fbbf24' : '#b45309',
                  cursor: 'pointer',
                  '& .MuiChip-icon': { color: darkMode ? '#fbbf24' : '#b45309' },
                  '&:hover': { backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.3)' : 'rgba(180, 83, 9, 0.2)' }
                }}
              />
            )}
            {well.county && (
              <Chip
                label={well.county}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                  color: cyber.chipGreen,
                }}
              />
            )}
            {/* State Badge (TX/NM) */}
            {well.state && (
              <Chip
                label={well.state}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: well.state === 'TX' 
                    ? (darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(127, 29, 29, 0.15)')
                    : (darkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(30, 64, 175, 0.15)'),
                  color: well.state === 'TX' 
                    ? (darkMode ? '#ef4444' : '#b91c1c')
                    : (darkMode ? '#3b82f6' : '#1e40af'),
                  border: `1px solid ${well.state === 'TX' 
                    ? (darkMode ? '#ef4444' : '#dc2626')
                    : (darkMode ? '#3b82f6' : '#1d4ed8')
                  }`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              />
            )}
            {well.operator && (
              <Chip
                label={well.operator}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                  color: darkMode ? '#ffffff' : '#334155',
                  maxWidth: 140,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            )}
            {!isActiveStatus && well.status && (
              <Box sx={{
                backgroundColor: statusColors.bg,
                border: `1px solid ${statusColors.border}`,
                borderRadius: '6px',
                px: 0.5,
                py: 0.15,
                display: 'flex',
                alignItems: 'center',
                gap: 0.35
              }}>
                <Box sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: statusColors.dot,
                  boxShadow: statusColors.dotGlow
                }} />
                <Typography variant="caption" sx={{ 
                  color: statusColors.text, 
                  fontWeight: 700, 
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif'
                }}>
                  {well.status}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ROW 3: Action Buttons - match tank battery card layout (Fav, Map, Share, Google, Photos) */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.5, mt: 0.5, pt: 0.5 }}>
            {/* Favorite */}
            <Tooltip title={favorited ? 'Remove from Favorites' : 'Add to Favorites'} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  telegram.hapticFeedback('light');
                  onToggleFavorite(well);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  color: favorited ? cyber.neonGold : cyber.textMuted,
                  backgroundColor: favorited ? 'rgba(251, 191, 36, 0.12)' : 'transparent',
                  '&:hover': { color: cyber.neonGold, backgroundColor: 'rgba(251, 191, 36, 0.2)' }
                }}
              >
                {favorited ? <StarIcon sx={{ fontSize: '1.25rem' }} /> : <StarBorderIcon sx={{ fontSize: '1.25rem' }} />}
              </IconButton>
            </Tooltip>

            {/* View on Map */}
            <Tooltip title="View on Map" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  telegram.hapticFeedback('light');
                  if (onViewOnMap) {
                    onViewOnMap(well);
                  } else {
                    onSelectWell(well);
                  }
                }}
                sx={{
                  width: 40,
                  height: 40,
                  color: cyber.neonBlue,
                  backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.12)' : 'rgba(30, 64, 175, 0.1)',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(30, 64, 175, 0.2)' }
                }}
              >
                <MapIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>

            {/* Share */}
            <Tooltip title="Share" arrow>
              <IconButton
                size="small"
                onClick={(e) => handleShare(well, e)}
                sx={{
                  width: 40,
                  height: 40,
                  color: darkMode ? '#22c55e' : '#16a34a',
                  backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.22)' : 'rgba(34, 197, 94, 0.18)' }
                }}
              >
                <ShareIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>

            {/* Google Maps */}
            <Tooltip title="Google Maps" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  telegram.hapticFeedback('light');
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${well.latitude},${well.longitude}&travelmode=driving`, '_blank');
                }}
                sx={{
                  width: 40,
                  height: 40,
                  color: '#4285F4',
                  backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.15)' : 'rgba(66, 133, 244, 0.1)',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.3)' : 'rgba(66, 133, 244, 0.2)' }
                }}
              >
                <GoogleIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>

            {/* Photos */}
            <Tooltip title="Photos" arrow>
              <IconButton
                size="small"
                onClick={(e) => handleOpenPhotos(well, e)}
                sx={{
                  width: 40,
                  height: 40,
                  color: cyber.neonPink,
                  backgroundColor: `${cyber.neonPink}20`,
                  '&:hover': { backgroundColor: `${cyber.neonPink}40`, boxShadow: `0 0 12px ${cyber.neonPink}60` }
                }}
              >
                <PhotoLibraryIcon sx={{ fontSize: '1.25rem' }} />
              </IconButton>
            </Tooltip>

          </Box>
        </Box>
      </Box>
    );
  }, [letterFilteredWells, isFavorite, onSelectWell, onViewOnMap, onToggleFavorite, darkMode, cyber, handleCopyApi, handleShare, handleOpenPhotos]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          border: `1px solid ${cyber.borderDefault}`,
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header - Matching SearchResults Design */}
      <DialogTitle sx={{ color: cyber.textPrimary, pb: 0, flexShrink: 0 }}>
        {/* Header Row - Search Info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: cyber.textSecondary }}>
            Browse Wells • <span style={{ color: cyber.neonBlue, fontWeight: 600 }}>{letterFilteredWells.length.toLocaleString()}</span> results
            <span style={{ color: cyber.neonGreen, marginLeft: '8px' }}>
              {loading ? (loadProgress > 0 ? `Loading ${Math.round(loadProgress * 100)}%...` : 'Loading...') : 
                `📦 ${allWells.length.toLocaleString()} ${dataSource === 'cache' ? 'cached' : 'loaded'}`}
            </span>
          </Typography>
        </Box>

        {/* Filter Controls Row */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          pb: 2,
          borderBottom: `1px solid ${cyber.borderDefault}`
        }}>
          {/* Left: Active Filters Info */}
          {activeFiltersCount > 0 && (
            <Typography variant="caption" sx={{ color: cyber.neonGreen, fontWeight: 600 }}>
              {activeFiltersCount} {activeFiltersCount === 1 ? 'filter' : 'filters'}: 
              <span style={{ color: cyber.neonGreen, fontWeight: 700, marginLeft: '4px' }}>
                {[
                  statusFilter !== 'all' && statusFilter,
                  countyFilter !== 'all' && countyFilter,
                  operatorFilter !== 'all' && operatorFilter
                ].filter(Boolean).join(' • ')}
              </span>
            </Typography>
          )}
          
          {/* Right: Filter Buttons */}
          <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
            {/* Status Filter */}
            <Tooltip title="Filter by Status">
              <Badge badgeContent={statusFilter !== 'all' ? '1' : 0} color="success">
                <IconButton 
                  onClick={handleStatusFilterClick}
                  sx={{ 
                    width: 44,
                    height: 44,
                    color: statusFilter !== 'all' ? cyber.neonGreen : cyber.textMuted,
                    backgroundColor: statusFilter !== 'all' ? `${cyber.neonGreen}10` : 'transparent',
                    border: `1px solid ${cyber.borderDefault}`,
                    '&:hover': { 
                      backgroundColor: `${cyber.neonBlue}10`,
                      borderColor: cyber.borderHover
                    }
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Badge>
            </Tooltip>

            {/* County Filter */}
            <Tooltip title="Filter by County">
              <Badge badgeContent={countyFilter !== 'all' ? '1' : 0} color="success">
                <IconButton 
                  onClick={handleCountyFilterClick}
                  sx={{ 
                    width: 44,
                    height: 44,
                    color: countyFilter !== 'all' ? cyber.neonGreen : cyber.textMuted,
                    backgroundColor: countyFilter !== 'all' ? `${cyber.neonGreen}10` : 'transparent',
                    border: `1px solid ${cyber.borderDefault}`,
                    '&:hover': { 
                      backgroundColor: `${cyber.neonBlue}10`,
                      borderColor: cyber.borderHover
                    }
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Badge>
            </Tooltip>

            {/* Operator Filter */}
            <Tooltip title="Filter by Operator">
              <Badge badgeContent={operatorFilter !== 'all' ? '1' : 0} color="success">
                <IconButton 
                  onClick={handleOperatorFilterClick}
                  sx={{ 
                    width: 44,
                    height: 44,
                    color: operatorFilter !== 'all' ? cyber.neonGreen : cyber.textMuted,
                    backgroundColor: operatorFilter !== 'all' ? `${cyber.neonGreen}10` : 'transparent',
                    border: `1px solid ${cyber.borderDefault}`,
                    '&:hover': { 
                      backgroundColor: `${cyber.neonBlue}10`,
                      borderColor: cyber.borderHover
                    }
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Badge>
            </Tooltip>

            {/* Sort Button */}
            <Tooltip title={sortOrder === 'asc' ? 'Sort Z-A' : 'Sort A-Z'}>
              <IconButton 
                onClick={handleToggleSort}
                sx={{ 
                  width: 44,
                  height: 44,
                  color: cyber.textMuted,
                  border: `1px solid ${cyber.borderDefault}`,
                  '&:hover': { 
                    backgroundColor: `${cyber.neonBlue}10`,
                    color: cyber.neonBlue,
                    borderColor: cyber.borderHover
                  }
                }}
              >
                <SortByAlphaIcon sx={{ 
                  fontSize: 20,
                  transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none',
                  transition: 'transform 0.3s ease'
                }} />
              </IconButton>
            </Tooltip>

            {/* Close Button */}
            <Tooltip title="Close">
              <IconButton 
                onClick={() => { telegram.hapticFeedback('light'); onClose(); }}
                sx={{ 
                  width: 44,
                  height: 44,
                  color: cyber.textMuted,
                  border: `1px solid ${cyber.borderDefault}`,
                  '&:hover': { 
                    backgroundColor: `${cyber.neonPink}10`,
                    color: cyber.neonPink,
                    borderColor: 'rgba(239, 68, 68, 0.3)'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      {/* Letter Drilldown - Compact SWD Style with Glove-Friendly Chevrons */}
      <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: `1px solid ${cyber.borderDefault}` }}>
        {/* Left: Breadcrumb + Count + Clear */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: cyber.textSecondary, fontWeight: 700, fontSize: '0.75rem' }}>
              {letterBreadcrumb}
            </Typography>
            <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.7rem', ml: 1 }}>
              {letterFilteredWells.length.toLocaleString()}/{filteredWells.length.toLocaleString()}
            </Typography>
          </Box>
          
          {/* Clear Button - appears when any letter is selected */}
          {selectedLetter && (
            <IconButton
              onClick={() => {
                telegram.hapticFeedback('medium');
                setSelectedLetter('');
                setSelectedSecondLetter('');
                setSelectedThirdLetter('');
                setSelectedFourthLetter('');
                setLetterStage('first');
              }}
              sx={{
                width: 36,
                height: 36,
                color: '#f97316',
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                border: '2px solid rgba(249, 115, 22, 0.5)',
                borderRadius: 2,
                '&:hover': { backgroundColor: 'rgba(249, 115, 22, 0.2)' },
                '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <ClearAllIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
        
        {/* Right: Glove-Friendly Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Expand/Collapse */}
          <IconButton
            onClick={() => setLetterExpanded((v) => !v)}
            sx={{ 
              width: 44, 
              height: 44, 
              color: letterExpanded ? cyber.neonBlue : cyber.textSecondary,
              backgroundColor: letterExpanded ? `${cyber.neonBlue}15` : 'transparent',
              border: `2px solid ${letterExpanded ? cyber.neonBlue : cyber.borderDefault}`,
              borderRadius: 2,
              '&:hover': { backgroundColor: `${cyber.neonBlue}20` }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 24, transform: letterExpanded ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }} />
          </IconButton>
          
          {/* Previous Stage */}
          <IconButton
            onClick={() => handleLetterStageChange(-1)}
            disabled={letterStageIndex === 0}
            sx={{ 
              width: 52, 
              height: 52, 
              color: letterStageIndex === 0 ? cyber.textMuted : cyber.neonCyan,
              backgroundColor: letterStageIndex === 0 ? 'transparent' : `${cyber.neonCyan}10`,
              border: `2px solid ${letterStageIndex === 0 ? cyber.borderDefault : cyber.neonCyan}`,
              borderRadius: 2,
              '&:hover:not(:disabled)': { backgroundColor: `${cyber.neonCyan}25` },
              '&:active:not(:disabled)': { transform: 'scale(0.95)' }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 28 }} />
          </IconButton>
          
          {/* Current Stage Label */}
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              border: `2px solid ${cyber.neonBlue}`,
              backgroundColor: `${cyber.neonBlue}15`,
              color: cyber.neonBlue,
              minWidth: 70,
              textAlign: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{letterStageLabels[letterStage]}</Typography>
          </Box>
          
          {/* Next Stage */}
          <IconButton
            onClick={() => handleLetterStageChange(1)}
            disabled={letterStageIndex >= maxLetterStageIndex}
            sx={{ 
              width: 52, 
              height: 52, 
              color: letterStageIndex >= maxLetterStageIndex ? cyber.textMuted : cyber.neonCyan,
              backgroundColor: letterStageIndex >= maxLetterStageIndex ? 'transparent' : `${cyber.neonCyan}10`,
              border: `2px solid ${letterStageIndex >= maxLetterStageIndex ? cyber.borderDefault : cyber.neonCyan}`,
              borderRadius: 2,
              '&:hover:not(:disabled)': { backgroundColor: `${cyber.neonCyan}25` },
              '&:active:not(:disabled)': { transform: 'scale(0.95)' }
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>
      
      {/* Expanded Letter Chips */}
      {letterExpanded && (
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${cyber.borderDefault}` }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            overflowX: 'auto',
            pb: 0.5,
            scrollbarWidth: 'thin',
            scrollbarColor: darkMode ? 'rgba(96, 165, 250, 0.3) transparent' : 'rgba(100, 116, 139, 0.3) transparent',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { 
              backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(100, 116, 139, 0.3)', 
              borderRadius: 8 
            }
          }}>
            {/* Stage 1: First letter selection */}
            {letterStage === 'first' && (
              <>
                <Box
                  onClick={() => {
                    telegram.hapticFeedback('light');
                    setSelectedLetter('');
                    setSelectedSecondLetter('');
                    setSelectedThirdLetter('');
                    setSelectedFourthLetter('');
                  }}
                  sx={{
                    px: 1, py: 0.5, borderRadius: '12px',
                    border: `1px solid ${selectedLetter === '' ? cyber.neonBlue : cyber.borderDefault}`,
                    backgroundColor: selectedLetter === '' ? `${cyber.neonBlue}15` : cyber.bgCard,
                    color: selectedLetter === '' ? cyber.neonBlue : cyber.textSecondary,
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    minHeight: 34, minWidth: 42, flexShrink: 0,
                    '&:hover': { borderColor: cyber.borderHover }
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>all</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{filteredWells.length.toLocaleString()}</Typography>
                </Box>
                {Object.keys(letterStats.lCounts).sort((a, b) => a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)).map((letter) => (
                  <Box
                    key={letter}
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setSelectedLetter(letter);
                      setSelectedSecondLetter('');
                      setSelectedThirdLetter('');
                      setSelectedFourthLetter('');
                      if (Object.keys(secondCounts[letter] || {}).length > 0) {
                        setLetterStage('second');
                      }
                    }}
                    sx={{
                      px: 1, py: 0.5, borderRadius: '12px',
                      border: `1px solid ${selectedLetter === letter ? cyber.neonBlue : cyber.borderDefault}`,
                      backgroundColor: selectedLetter === letter ? `${cyber.neonBlue}15` : cyber.bgCard,
                      color: selectedLetter === letter ? cyber.neonBlue : cyber.textSecondary,
                      display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      minHeight: 34, minWidth: 42, flexShrink: 0,
                      '&:hover': { borderColor: cyber.borderHover }
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(letterStats.lCounts[letter] || 0).toLocaleString()}</Typography>
                  </Box>
                ))}
              </>
            )}
            
            {/* Stage 2: Second letter selection */}
            {letterStage === 'second' && selectedLetter && (
              <>
                <Box
                  onClick={() => {
                    telegram.hapticFeedback('light');
                    setSelectedSecondLetter('');
                    setSelectedThirdLetter('');
                    setSelectedFourthLetter('');
                  }}
                  sx={{
                    px: 1, py: 0.5, borderRadius: '12px',
                    border: `1px solid ${selectedSecondLetter === '' ? cyber.neonBlue : cyber.borderDefault}`,
                    backgroundColor: selectedSecondLetter === '' ? `${cyber.neonBlue}15` : cyber.bgCard,
                    color: selectedSecondLetter === '' ? cyber.neonBlue : cyber.textSecondary,
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    minHeight: 34, flexShrink: 0,
                    '&:hover': { borderColor: cyber.borderHover }
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>All {selectedLetter}*</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(letterStats.lCounts[selectedLetter] || 0).toLocaleString()}</Typography>
                </Box>
                {availableSecondLetters.map((letter) => (
                  <Box
                    key={letter}
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setSelectedSecondLetter(letter);
                      setSelectedThirdLetter('');
                      setSelectedFourthLetter('');
                      if (Object.keys(thirdCounts[selectedLetter]?.[letter] || {}).length > 0) {
                        setLetterStage('third');
                      }
                    }}
                    sx={{
                      px: 1, py: 0.5, borderRadius: '12px',
                      border: `1px solid ${selectedSecondLetter === letter ? cyber.neonBlue : cyber.borderDefault}`,
                      backgroundColor: selectedSecondLetter === letter ? `${cyber.neonBlue}15` : cyber.bgCard,
                      color: selectedSecondLetter === letter ? cyber.neonBlue : cyber.textSecondary,
                      display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      minHeight: 34, minWidth: 42, flexShrink: 0,
                      '&:hover': { borderColor: cyber.borderHover }
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(secondCounts[selectedLetter]?.[letter] || 0).toLocaleString()}</Typography>
                  </Box>
                ))}
              </>
            )}
            
            {/* Stage 3: Third letter selection */}
            {letterStage === 'third' && selectedLetter && selectedSecondLetter && (
              <>
                <Box
                  onClick={() => {
                    telegram.hapticFeedback('light');
                    setSelectedThirdLetter('');
                    setSelectedFourthLetter('');
                  }}
                  sx={{
                    px: 1, py: 0.5, borderRadius: '12px',
                    border: `1px solid ${selectedThirdLetter === '' ? cyber.neonBlue : cyber.borderDefault}`,
                    backgroundColor: selectedThirdLetter === '' ? `${cyber.neonBlue}15` : cyber.bgCard,
                    color: selectedThirdLetter === '' ? cyber.neonBlue : cyber.textSecondary,
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    minHeight: 34, flexShrink: 0,
                    '&:hover': { borderColor: cyber.borderHover }
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>All {selectedLetter}{selectedSecondLetter}*</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(secondCounts[selectedLetter]?.[selectedSecondLetter] || 0).toLocaleString()}</Typography>
                </Box>
                {availableThirdLetters.map((letter) => (
                  <Box
                    key={letter}
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setSelectedThirdLetter(letter);
                      setSelectedFourthLetter('');
                      if (Object.keys(fourthCounts[selectedLetter]?.[selectedSecondLetter]?.[letter] || {}).length > 0) {
                        setLetterStage('fourth');
                      }
                    }}
                    sx={{
                      px: 1, py: 0.5, borderRadius: '12px',
                      border: `1px solid ${selectedThirdLetter === letter ? cyber.neonBlue : cyber.borderDefault}`,
                      backgroundColor: selectedThirdLetter === letter ? `${cyber.neonBlue}15` : cyber.bgCard,
                      color: selectedThirdLetter === letter ? cyber.neonBlue : cyber.textSecondary,
                      display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      minHeight: 34, minWidth: 42, flexShrink: 0,
                      '&:hover': { borderColor: cyber.borderHover }
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(thirdCounts[selectedLetter]?.[selectedSecondLetter]?.[letter] || 0).toLocaleString()}</Typography>
                  </Box>
                ))}
              </>
            )}
            
            {/* Stage 4: Fourth letter selection */}
            {letterStage === 'fourth' && selectedLetter && selectedSecondLetter && selectedThirdLetter && (
              <>
                <Box
                  onClick={() => {
                    telegram.hapticFeedback('light');
                    setSelectedFourthLetter('');
                  }}
                  sx={{
                    px: 1, py: 0.5, borderRadius: '12px',
                    border: `1px solid ${selectedFourthLetter === '' ? cyber.neonBlue : cyber.borderDefault}`,
                    backgroundColor: selectedFourthLetter === '' ? `${cyber.neonBlue}15` : cyber.bgCard,
                    color: selectedFourthLetter === '' ? cyber.neonBlue : cyber.textSecondary,
                    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                    minHeight: 34, flexShrink: 0,
                    '&:hover': { borderColor: cyber.borderHover }
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>All {selectedLetter}{selectedSecondLetter}{selectedThirdLetter}*</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(thirdCounts[selectedLetter]?.[selectedSecondLetter]?.[selectedThirdLetter] || 0).toLocaleString()}</Typography>
                </Box>
                {availableFourthLetters.map((letter) => (
                  <Box
                    key={letter}
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      setSelectedFourthLetter(letter);
                    }}
                    sx={{
                      px: 1, py: 0.5, borderRadius: '12px',
                      border: `1px solid ${selectedFourthLetter === letter ? cyber.neonBlue : cyber.borderDefault}`,
                      backgroundColor: selectedFourthLetter === letter ? `${cyber.neonBlue}15` : cyber.bgCard,
                      color: selectedFourthLetter === letter ? cyber.neonBlue : cyber.textSecondary,
                      display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                      minHeight: 34, minWidth: 42, flexShrink: 0,
                      '&:hover': { borderColor: cyber.borderHover }
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(fourthCounts[selectedLetter]?.[selectedSecondLetter]?.[selectedThirdLetter]?.[letter] || 0).toLocaleString()}</Typography>
                  </Box>
                ))}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Wells List with Alphabet Scrubber */}
      <DialogContent ref={containerRef} sx={{ p: 0, flex: 1, overflow: 'hidden', display: 'flex', position: 'relative' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', gap: 2 }}>
            <CyberSpinner size={50} />
            {loadProgress > 0 && (
              <Typography variant="body2" sx={{ color: cyber.textMuted }}>
                Loading wells... {Math.round(loadProgress * 100)}%
              </Typography>
            )}
          </Box>
        ) : allWells.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', gap: 2, px: 3, textAlign: 'center' }}>
            <StorageIcon sx={{ fontSize: 48, color: cyber.textMuted }} />
            <Typography variant="h6" sx={{ color: cyber.textSecondary }}>
              No cached wells
            </Typography>
            <Typography variant="body2" sx={{ color: cyber.textMuted }}>
              Download counties in Settings → Offline & Cache to browse wells offline.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Virtualized List with themed scrollbar */}
            <Box 
              sx={{ 
                flex: 1, 
                pr: '48px', // Space for alphabet scrubber
                overflow: 'hidden', 
                position: 'relative',
                '& > div': {
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { 
                    backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.5)' : 'rgba(100, 116, 139, 0.5)',
                    borderRadius: 3
                  },
                  '&::-webkit-scrollbar-thumb:hover': { 
                    backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.7)' : 'rgba(100, 116, 139, 0.7)'
                  }
                }
              }}
            >
              <List<Record<string, never>>
                listRef={listRef}
                rowComponent={WellRow}
                rowCount={letterFilteredWells.length}
                rowHeight={ITEM_HEIGHT}
                rowProps={{}}
                onRowsRendered={handleRowsRendered}
                style={{ 
                  height: listHeight || containerRef.current?.clientHeight || 400, 
                  width: '100%',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: darkMode ? 'rgba(96, 165, 250, 0.5) transparent' : 'rgba(100, 116, 139, 0.5) transparent'
                }}
              />
            </Box>
            
            {/* Floating Letter Badge */}
            {showLetterBadge && currentLetter && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '40%',
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  border: `2px solid ${cyber.neonBlue}`,
                  borderRadius: '16px',
                  px: 4,
                  py: 2,
                  zIndex: 100,
                  boxShadow: `0 8px 32px ${cyber.neonBlue}40`,
                  pointerEvents: 'none'
                }}
              >
                <Typography
                  sx={{
                    color: cyber.neonBlue,
                    fontSize: '2.5rem',
                    fontWeight: 800,
                    textShadow: `0 0 20px ${cyber.neonBlue}`
                  }}
                >
                  {currentLetter}
                </Typography>
              </Box>
            )}
            
            {/* Alphabet Scrubber - Right Side (matching SearchResults styling) */}
            {Object.keys(letterStats.lCounts).length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: 8,
                  bottom: 8,
                  width: 44,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 0,
                  zIndex: 50,
                  backgroundColor: 'rgba(15, 23, 42, 0.9)',
                  borderRadius: '16px',
                  py: 1,
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' }
                }}
              >
                {Object.keys(letterStats.lCounts).sort((a, b) => a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)).map((letter) => (
                  <Box
                    key={letter}
                    onClick={() => {
                      telegram.hapticFeedback('light');
                      handleLetterClick(letter);
                    }}
                    sx={{
                      width: 40,
                      height: 36,
                      minHeight: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: selectedLetter === letter ? '#60a5fa' : '#94a3b8',
                      fontWeight: selectedLetter === letter ? 700 : 600,
                      fontSize: '1rem',
                      transition: 'all 0.15s ease',
                      borderRadius: '8px',
                      flexShrink: 0,
                      backgroundColor: selectedLetter === letter ? 'rgba(96, 165, 250, 0.2)' : 'transparent',
                      '&:hover': {
                        color: '#60a5fa',
                        backgroundColor: 'rgba(96, 165, 250, 0.25)'
                      },
                      '&:active': {
                        backgroundColor: 'rgba(96, 165, 250, 0.5)',
                        transform: 'scale(1.15)'
                      }
                    }}
                  >
                    {letter}
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {/* Toast for copy feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="success" sx={{ backgroundColor: 'rgba(34, 197, 94, 0.9)', color: 'white' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Status Filter Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cyber.borderDefault}`,
            boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
            maxHeight: 300
          }
        }}
      >
        <MenuItem 
          onClick={() => handleStatusFilterChange('all')}
          selected={statusFilter === 'all'}
          sx={{ 
            color: cyber.textPrimary,
            '&.Mui-selected': {
              backgroundColor: `${cyber.neonBlue}20`,
              '&:hover': { backgroundColor: `${cyber.neonBlue}30` }
            },
            '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          All Statuses ({allWells.length})
        </MenuItem>
        {uniqueStatuses.map((status) => {
          const statusColor = (() => {
            const s = (status || '').toLowerCase();
            if (s.includes('inactive') || s.includes('closure') || s.includes('closed') || 
                s.includes('abandoned') || s.includes('plugged') || s.includes('cancelled')) return '#ef4444';
            if (s.includes('pending') || s.includes('request') || s.includes('submission')) return '#f59e0b';
            if (s.includes('active') || s.includes('producing') || s.includes('approved') || 
                s.includes('completed')) return '#10b981';
            return cyber.neonBlue;
          })();
          const count = allWells.filter((w: Well) => w.status?.toLowerCase() === status?.toLowerCase()).length;
          return (
            <MenuItem 
              key={status}
              onClick={() => handleStatusFilterChange(status!)}
              selected={statusFilter === status}
              sx={{ 
                color: statusColor,
                '&.Mui-selected': { backgroundColor: `${statusColor}20` },
                '&:hover': { backgroundColor: `${statusColor}10` }
              }}
            >
              {status} ({count})
            </MenuItem>
          );
        })}
      </Menu>

      {/* County Filter Menu */}
      <Menu
        anchorEl={countyAnchorEl}
        open={Boolean(countyAnchorEl)}
        onClose={handleCountyFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cyber.borderDefault}`,
            boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
            maxHeight: 300
          }
        }}
      >
        <MenuItem 
          onClick={() => handleCountyFilterChange('all')}
          selected={countyFilter === 'all'}
          sx={{ 
            color: cyber.textPrimary,
            '&.Mui-selected': { backgroundColor: `${cyber.neonBlue}20` },
            '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          All Counties ({allWells.length})
        </MenuItem>
        {uniqueCounties.map((county) => {
          const count = allWells.filter((w: Well) => w.county?.toLowerCase() === county?.toLowerCase()).length;
          return (
            <MenuItem 
              key={county}
              onClick={() => handleCountyFilterChange(county!)}
              selected={countyFilter === county}
              sx={{ 
                color: cyber.textPrimary,
                '&.Mui-selected': { backgroundColor: `${cyber.neonGreen}20` },
                '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              {county} ({count})
            </MenuItem>
          );
        })}
      </Menu>

      {/* Operator Filter Menu */}
      <Menu
        anchorEl={operatorAnchorEl}
        open={Boolean(operatorAnchorEl)}
        onClose={handleOperatorFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cyber.borderDefault}`,
            boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
            maxHeight: 300
          }
        }}
      >
        <MenuItem 
          onClick={() => handleOperatorFilterChange('all')}
          selected={operatorFilter === 'all'}
          sx={{ 
            color: cyber.textPrimary,
            '&.Mui-selected': { backgroundColor: `${cyber.neonBlue}20` },
            '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          All Operators ({allWells.length})
        </MenuItem>
        {uniqueOperators.slice(0, 50).map((operator) => {
          const count = allWells.filter((w: Well) => w.operator?.toLowerCase() === operator?.toLowerCase()).length;
          return (
            <MenuItem 
              key={operator}
              onClick={() => handleOperatorFilterChange(operator!)}
              selected={operatorFilter === operator}
              sx={{ 
                color: cyber.textPrimary,
                '&.Mui-selected': { backgroundColor: `${cyber.neonGreen}20` },
                '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              {operator} ({count})
            </MenuItem>
          );
        })}
        {uniqueOperators.length > 50 && (
          <MenuItem disabled sx={{ color: cyber.textMuted, fontSize: '0.85rem' }}>
            ... and {uniqueOperators.length - 50} more operators
          </MenuItem>
        )}
      </Menu>
    </Dialog>
  );
}
